import type { HelpCategory } from "../../types";

export const start: HelpCategory = {
  id: "start",
  title: "Начало работы",
  description: "Как устроен Coca, первый запуск и порядок настройки заведения.",
  icon: "rocket",
  articles: [
    {
      id: "overview",
      title: "Как устроен Coca",
      summary: "Из чего состоит система: программа управления (ERP), касса, экран кухни, сайт и QR-меню — и как они связаны.",
      keywords: ["что такое", "обзор", "система", "модули"],
      related: ["onboarding", "launch-checklist", "navigation"],
      body: [
        { t: "p", text: "Coca — одна система для всего заведения. Всё, что вы меняете в одном месте, сразу видно в остальных: поменяли цену в меню — через несколько секунд она на кассе, на сайте и в QR-меню." },
        {
          t: "terms", title: "Части системы",
          items: [
            { term: "Программа управления (ERP)", text: "То, что вы видите сейчас: меню, склад, сотрудники, отчёты, настройки. Работает в браузере на компьютере, планшете и телефоне." },
            { term: "Касса", text: "Раздел [«Касса»](/pos): заказы, оплата, скидки, отправка на кухню. Можно работать в браузере или в программе кассы Kafe Coco для компьютера (работает и без интернета)." },
            { term: "Экран кухни (KDS)", text: "Раздел [«Кухня»](/kitchen): повара видят заказы по своим станциям и отмечают «готовится» и «готово»." },
            { term: "Сайт и QR-меню", text: "Публичный сайт заведения и меню по QR-коду на столе. Гость может заказать с телефона — заказ приходит на кассу и на кухню." },
            { term: "Терминалы", text: "Устройства заведения (касса, экран кухни, планшет официанта). На них сотрудники входят по PIN-коду, без пароля." },
          ],
        },
        {
          t: "terms", title: "Главные понятия",
          items: [
            { term: "Компания", text: "Ваш бизнес целиком. Клиенты, продукты, поставщики и роли — общие для всей компании." },
            { term: "Филиал", text: "Отдельная точка (кафе, ресторан). У филиала свои столы, кассы, склад, смены и отчёты. Текущий филиал выбирается в шапке справа." },
            { term: "Роль", text: "Набор прав: что сотрудник может видеть и делать. Кассир не увидит зарплаты, бухгалтер — кассу. См. [[users-roles]]." },
            { term: "Тариф и дополнения", text: "От тарифа зависит, какие разделы доступны. Раздел с замком в меню не входит в тариф — его можно подключить как дополнение. См. [[subscription]]." },
          ],
        },
        { t: "tip", text: "У каждого раздела есть кнопка **«?»** в шапке — она открывает статью справочника именно про этот раздел. А значок **ⓘ** рядом с непонятными полями показывает короткую подсказку." },
      ],
    },
    {
      id: "onboarding",
      title: "Первый запуск: мастер настройки",
      summary: "После регистрации мастер за несколько шагов настраивает компанию, филиал, валюту, меню, столы и сайт.",
      path: "/onboarding",
      permission: "settings.manage",
      keywords: ["регистрация", "мастер", "старт", "настройка"],
      related: ["launch-checklist", "company-branches"],
      body: [
        { t: "p", text: "Сразу после регистрации владелец попадает в мастер настройки. Обязательны только первые шаги — остальное можно пропустить и вернуться позже." },
        {
          t: "steps", title: "Шаги мастера",
          items: [
            "**Компания** — название, страна, часовой пояс. Часовой пояс важен: по нему считается «сегодня» в отчётах и сменах.",
            "**Филиал** — название точки, адрес, форматы работы (в зале, с собой, доставка).",
            "**Валюта и налог** — валюта цен и ставка налога, включён ли налог в цену.",
            "**Меню** — можно загрузить демо-меню под ваш формат (кофейня, пиццерия, ресторан) и потом поправить, или начать с пустого.",
            "**Столы** — генератор столов: сколько залов и столов, нумерация. QR-коды для столов создадутся сами.",
            "**Сайт** — адрес сайта заведения ({slug}.cafeos.kg), описание и фото.",
          ],
        },
        { t: "tip", text: "Мастер можно пройти ещё раз — его шаги не удаляют то, что вы уже ввели." },
        { t: "warn", text: "Мастер видят только сотрудники с правом управлять настройками (владелец, администратор). Остальные сразу попадают в свои разделы." },
      ],
    },
    {
      id: "launch-checklist",
      title: "Чек-лист: запускаем заведение за один день",
      summary: "Порядок действий, чтобы касса, кухня, склад и персонал заработали правильно с первого дня.",
      keywords: ["запуск", "чек-лист", "порядок", "с чего начать"],
      related: ["onboarding", "menu-items", "recipes", "users-roles", "cash-shifts"],
      body: [
        { t: "p", text: "Ниже — проверенный порядок. Каждый пункт ведёт в нужный раздел и статью справочника." },
        {
          t: "steps", title: "Утро: основа",
          items: [
            "Проверьте [«Компанию»](/settings/company) и [«Филиалы»](/branches): часовой пояс, валюта, налог, время начала бизнес-дня. См. [[company-branches]].",
            "Заведите [«Продукты»](/products) — то, что закупаете: мука, молоко, стаканы. См. [[products]].",
            "Создайте [«Меню»](/menu): категории, позиции, цены, модификаторы. См. [[menu-items]].",
            "Для каждого блюда сделайте техкарту (ТТК) — без неё склад не будет списывать продукты при продаже. См. [[recipes]].",
            "Проверьте [«Залы и столы»](/tables) и [«Кухонные станции»](/kitchen-stations): какое блюдо на какой экран кухни уходит. См. [[tables-halls]], [[kds]].",
          ],
        },
        {
          t: "steps", title: "День: люди и устройства",
          items: [
            "Создайте [«Роли»](/staff/roles), если стандартных не хватает, и добавьте [«Пользователей»](/staff/users). См. [[users-roles]].",
            "Заведите [«Кадры»](/staff/employees): должность, схема оплаты, PIN для входа на терминале. См. [[employees-positions]].",
            "Привяжите [«Терминалы»](/settings/devices): кассу, экран кухни, планшеты. См. [[terminals]].",
            "Проведите первую [«Закупку»](/purchases) или [«Инвентаризацию»](/inventory-counts), чтобы на складе появились остатки. См. [[purchases]].",
          ],
        },
        {
          t: "steps", title: "Вечер: пробный прогон",
          items: [
            "Откройте [кассовую смену](/cash-shifts) с разменом. См. [[cash-shifts]].",
            "Пробейте тестовый заказ на кассе, отправьте на кухню, примите оплату. См. [[pos-basics]].",
            "Проверьте, что продукты списались в [«Остатках»](/inventory), а заказ виден в [«Отчётах»](/reports).",
            "Закройте смену — посмотрите Z-отчёт. Тестовый заказ можно вернуть из карточки заказа.",
          ],
        },
        { t: "tip", text: "Подключите Telegram-бота в [«Уведомлениях»](/settings/notifications) — каждый вечер будет приходить отчёт за день. См. [[notifications]]." },
      ],
    },
    {
      id: "navigation",
      title: "Навигация: меню, филиал, язык, тема",
      summary: "Где что находится в программе, как переключить филиал, язык интерфейса и тёмную тему.",
      keywords: ["меню", "навигация", "язык", "тема", "филиал", "переключить"],
      related: ["overview", "notifications", "ai"],
      body: [
        { t: "video", id: "navigation", title: "Как устроен Coca: разделы и меню", duration: 26 },
        {
          t: "list", title: "Экран программы",
          items: [
            "**Тёмная панель слева** — группы разделов: аналитика, продажи, заведение, смены, каталог и склад, персонал, настройки. Внизу — быстрый вход в кассу и на кухню.",
            "**Второе меню** — разделы выбранной группы. Раздел с **замком** не входит в тариф: откроется описание и кнопка «Подключить».",
            "**Шапка**: выбор филиала, кнопка **AI**, колокольчик уведомлений, «?» — справочник по текущему разделу, и меню профиля (язык, тема, выход).",
            "**На телефоне** меню разделов — внизу экрана, кнопка «Ещё» открывает остальное.",
          ],
        },
        { t: "p", text: "**Филиал.** Почти все данные (заказы, склад, смены) показываются по выбранному филиалу. Если вы видите «пусто» — проверьте филиал в шапке." },
        { t: "p", text: "**Язык.** В меню профиля: русский, кыргызский, узбекский, английский. Язык сохраняется на этом устройстве." },
        { t: "p", text: "**Тема.** Светлая или тёмная — в меню профиля. Экран кухни всегда тёмный, чтобы не слепить поваров." },
        { t: "tip", text: "Разделы, на которые у вас нет прав, в меню не показываются. Если нужного раздела нет — попросите владельца добавить право в вашу роль." },
      ],
    },
  ],
};
